import {useEffect, useState} from 'react';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {Badge} from '@/components/ui/badge';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {toast} from 'sonner';
import {Plus, Edit, Trash2, Search, X} from 'lucide-react';
import {
    getAffiliates, createAffiliate, updateAffiliate, deleteAffiliate, getAffiliateMeta
} from '@/api/affiliate.api';

const initialForm = {
    fullName: '',
    mobileNumber: '',
    email: '',
    fullAddress: '',
    city: '',
    role: '',
    brandName: '',
    activeClients: '',
    bankName: '',
    ifscCode: '',
    accountNumber: '',
    promotionPlan: '',
    socialLinks: '',
    activeFollowers: '',
    contactMethod: '',
    agreeTerms: false,
    status: 'Pending Review',
    couponCode: '',
    wallet: 0,
    remarks: '',
    isSignAgreement: false,
    agreementVersion: '',
    isActivateSubscription: false,
    // Coupon/offer settings used only the first time this affiliate is
    // approved — auto-creates the linked restaurant offer + coupon code.
    offerScope: 'ALL',
    offerResturantIds: '',
    offerType: 'PERCENTAGE',
    offerValue: 10,
    offerMinOrder: 0,
    offerMaxDiscount: 0,
    offerTitle: ''
};

const statusBadgeClass = (s) => {
    switch (s) {
        case 'Approved':
            return 'bg-green-100 text-green-700';
        case 'Rejected':
            return 'bg-red-100 text-red-700';
        case 'Ban':
            return 'bg-red-200 text-red-800';
        case 'Dormant':
            return 'bg-gray-200 text-gray-700';
        default:
            return 'bg-yellow-100 text-yellow-800';
    }
};

const AffiliatesTab = () => {
    const [list, setList] = useState([]);
    const [meta, setMeta] = useState({roles: [], contactMethods: [], affiliateStatuses: []});
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(initialForm);

    const load = async () => {
        try {
            const [a, m] = await Promise.all([getAffiliates(), getAffiliateMeta()]);
            setList(a.data);
            setMeta(m.data);
        } catch {
            toast.error('Failed to load affiliates');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const [a, m] = await Promise.all([getAffiliates(), getAffiliateMeta()]);
                if (!mounted) return;
                setList(a.data);
                setMeta(m.data);
            } catch {
                if (mounted) toast.error('Failed to load affiliates');
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    const openAdd = () => {
        setEditId(null);
        setForm(initialForm);
        setOpen(true);
    };
    const openEdit = (a) => {
        setEditId(a.id);
        setForm({
            ...initialForm,
            ...a,
            role: a.role || '',
            contactMethod: a.contactMethod || '',
            wallet: a.wallet ?? 0
        });
        setOpen(true);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            ...form,
            role: form.role || null,
            contactMethod: form.contactMethod || null,
            wallet: Number(form.wallet) || 0,
            offerResturantIds: form.offerResturantIds
                ? form.offerResturantIds.split(',').map((s) => s.trim()).filter(Boolean)
                : []
        };
        try {
            if (editId) {
                await updateAffiliate(editId, payload);
                toast.success('Affiliate updated');
            } else {
                await createAffiliate(payload);
                toast.success('Affiliate created');
            }
            setOpen(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Save failed');
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm('Delete this affiliate?')) return;
        try {
            await deleteAffiliate(id);
            toast.success('Deleted');
            load();
        } catch {
            toast.error('Delete failed');
        }
    };

    const q = search.trim().toLowerCase();
    const filtered = list.filter((a) => {
        if (statusFilter !== 'all' && a.status !== statusFilter) return false;
        if (!q) return true;
        const dig = q.replace(/\D/g, '');
        return (
            (a.fullName || '').toLowerCase().includes(q) ||
            (a.email || '').toLowerCase().includes(q) ||
            (a.couponCode || '').toLowerCase().includes(q) ||
            (a.brandName || '').toLowerCase().includes(q) ||
            (dig && String(a.mobileNumber || '').replace(/\D/g, '').includes(dig))
        );
    });

    if (loading) {
        return (
            <div className="flex justify-center items-center h-48">
                <div className="animate-spin h-10 w-10 border-2 border-primary border-t-transparent rounded-full"/>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center flex-wrap gap-3">
                <h2 className="text-xl font-semibold">Foodk Affiliates</h2>
                <Button onClick={openAdd} data-testid="affiliate-add-button">
                    <Plus className="mr-2" size={18}/> Add Affiliate
                </Button>
            </div>

            <div className="flex flex-wrap gap-3 items-center">
                <div className="relative w-full sm:max-w-sm">
                    <Search size={16}
                            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                    <Input
                        placeholder="Search by name, phone, email, coupon..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9 pr-9"
                        data-testid="affiliate-search-input"
                    />
                    {search && (
                        <button type="button" onClick={() => setSearch('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100">
                            <X size={14}/>
                        </button>
                    )}
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-48"><SelectValue/></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {meta.affiliateStatuses.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="border rounded-lg overflow-x-auto">
                <table className="w-full min-w-[1000px]">
                    <thead className="bg-gray-100">
                    <tr>
                        <th className="p-3 text-left">Name</th>
                        <th className="p-3 text-left">Contact</th>
                        <th className="p-3 text-left">Role</th>
                        <th className="p-3 text-left">Brand</th>
                        <th className="p-3 text-left">Coupon</th>
                        <th className="p-3 text-right">Wallet</th>
                        <th className="p-3 text-center">Status</th>
                        <th className="p-3 text-right">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filtered.map((a) => (
                        <tr key={a.id} className="border-t hover:bg-gray-50">
                            <td className="p-3">
                                <div className="font-medium">{a.fullName || '-'}</div>
                                <div className="text-xs text-gray-500">{a.city || ''}</div>
                            </td>
                            <td className="p-3 text-sm">
                                <div>{a.mobileNumber || '-'}</div>
                                <div className="text-xs text-gray-500">{a.email || ''}</div>
                            </td>
                            <td className="p-3 text-sm">{a.role || '-'}</td>
                            <td className="p-3 text-sm">{a.brandName || '-'}</td>
                            <td className="p-3 text-sm font-mono">{a.couponCode || '-'}</td>
                            <td className="p-3 text-right">₹{a.wallet || 0}</td>
                            <td className="p-3 text-center">
                                <Badge className={`${statusBadgeClass(a.status)} hover:opacity-90`}>
                                    {a.status}
                                </Badge>
                            </td>
                            <td className="p-3 text-right flex justify-end gap-2">
                                <Button size="sm" variant="outline" onClick={() => openEdit(a)}
                                        data-testid={`affiliate-edit-${a.id}`}>
                                    <Edit size={14}/>
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => onDelete(a.id)}
                                        data-testid={`affiliate-delete-${a.id}`}>
                                    <Trash2 size={14}/>
                                </Button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
                {filtered.length === 0 && (
                    <p className="text-center text-gray-500 p-8">No affiliates found.</p>
                )}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editId ? 'Edit Affiliate' : 'Add Affiliate'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={onSubmit} className="space-y-5">
                        {/* Identity */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label>Full Name *</Label>
                                <Input value={form.fullName}
                                       onChange={(e) => setForm({...form, fullName: e.target.value})} required
                                       data-testid="affiliate-fullname"/>
                            </div>
                            <div>
                                <Label>Mobile Number *</Label>
                                <Input value={form.mobileNumber}
                                       onChange={(e) => setForm({...form, mobileNumber: e.target.value})} required
                                       data-testid="affiliate-mobile"/>
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input type="email" value={form.email}
                                       onChange={(e) => setForm({...form, email: e.target.value})}
                                       data-testid="affiliate-email"/>
                            </div>
                            <div>
                                <Label>City</Label>
                                <Input value={form.city}
                                       onChange={(e) => setForm({...form, city: e.target.value})}/>
                            </div>
                            <div className="col-span-2">
                                <Label>Full Address</Label>
                                <Input value={form.fullAddress}
                                       onChange={(e) => setForm({...form, fullAddress: e.target.value})}/>
                            </div>
                        </div>

                        {/* Role & brand */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t pt-4">
                            <div>
                                <Label>Role</Label>
                                <Select value={form.role || ''}
                                        onValueChange={(v) => setForm({...form, role: v === '__none__' ? '' : v})}>
                                    <SelectTrigger><SelectValue placeholder="Select role"/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">— None —</SelectItem>
                                        {meta.roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Brand Name</Label>
                                <Input value={form.brandName}
                                       onChange={(e) => setForm({...form, brandName: e.target.value})}/>
                            </div>
                            <div>
                                <Label>Active Clients</Label>
                                <Input value={form.activeClients}
                                       onChange={(e) => setForm({...form, activeClients: e.target.value})}/>
                            </div>
                            <div>
                                <Label>Active Followers</Label>
                                <Input value={form.activeFollowers}
                                       onChange={(e) => setForm({...form, activeFollowers: e.target.value})}/>
                            </div>
                            <div className="col-span-2">
                                <Label>Social Links</Label>
                                <Input value={form.socialLinks}
                                       onChange={(e) => setForm({...form, socialLinks: e.target.value})}/>
                            </div>
                            <div className="col-span-2">
                                <Label>Promotion Plan</Label>
                                <Input value={form.promotionPlan}
                                       onChange={(e) => setForm({...form, promotionPlan: e.target.value})}/>
                            </div>
                        </div>

                        {/* Bank */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t pt-4">
                            <div>
                                <Label>Bank Name</Label>
                                <Input value={form.bankName}
                                       onChange={(e) => setForm({...form, bankName: e.target.value})}/>
                            </div>
                            <div>
                                <Label>IFSC Code</Label>
                                <Input value={form.ifscCode}
                                       onChange={(e) => setForm({...form, ifscCode: e.target.value})}/>
                            </div>
                            <div className="col-span-2">
                                <Label>Account Number</Label>
                                <Input value={form.accountNumber}
                                       onChange={(e) => setForm({...form, accountNumber: e.target.value})}/>
                            </div>
                        </div>

                        {/* Admin */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t pt-4">
                            <div>
                                <Label>Contact Method</Label>
                                <Select value={form.contactMethod || ''}
                                        onValueChange={(v) => setForm({
                                            ...form,
                                            contactMethod: v === '__none__' ? '' : v
                                        })}>
                                    <SelectTrigger><SelectValue placeholder="Select"/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">— None —</SelectItem>
                                        {meta.contactMethods.map((c) => <SelectItem key={c}
                                                                                    value={c}>{c}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Status</Label>
                                <Select value={form.status}
                                        onValueChange={(v) => setForm({...form, status: v})}>
                                    <SelectTrigger><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        {meta.affiliateStatuses.map((s) => <SelectItem key={s}
                                                                                       value={s}>{s}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Coupon Code</Label>
                                <Input value={form.couponCode}
                                       onChange={(e) => setForm({...form, couponCode: e.target.value})}
                                       data-testid="affiliate-coupon"/>
                            </div>
                            <div>
                                <Label>Wallet (₹)</Label>
                                <Input type="number" min="0" value={form.wallet}
                                       onChange={(e) => setForm({...form, wallet: e.target.value})}/>
                            </div>
                            <div className="col-span-2">
                                <Label>Remarks</Label>
                                <Input value={form.remarks}
                                       onChange={(e) => setForm({...form, remarks: e.target.value})}/>
                            </div>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={!!form.agreeTerms}
                                       onChange={(e) => setForm({...form, agreeTerms: e.target.checked})}/>
                                Agreed to Terms
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={!!form.isSignAgreement}
                                       onChange={(e) => setForm({...form, isSignAgreement: e.target.checked})}/>
                                Signed Agreement
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={!!form.isActivateSubscription}
                                       onChange={(e) => setForm({
                                           ...form,
                                           isActivateSubscription: e.target.checked
                                       })}/>
                                Subscription Active
                            </label>
                            <div>
                                <Label>Agreement Version</Label>
                                <Input value={form.agreementVersion}
                                       onChange={(e) => setForm({...form, agreementVersion: e.target.value})}/>
                            </div>
                        </div>

                        {/* Coupon / offer settings — only used the first time this
                            affiliate is moved to Approved, to auto-generate their
                            coupon code and the restaurant offer it unlocks. */}
                        {form.status === 'Approved' && !form.couponCode && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t pt-4">
                                <p className="col-span-2 text-sm text-gray-500">
                                    Approving for the first time auto-generates a coupon code and
                                    creates the offer customers redeem it against. Configure it below.
                                </p>
                                <div>
                                    <Label>Applies To</Label>
                                    <Select value={form.offerScope}
                                            onValueChange={(v) => setForm({...form, offerScope: v})}>
                                        <SelectTrigger><SelectValue/></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="ALL">All restaurants</SelectItem>
                                            <SelectItem value="MULTIPLE">Multiple restaurants</SelectItem>
                                            <SelectItem value="SINGLE">Single restaurant</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                {form.offerScope !== 'ALL' && (
                                    <div>
                                        <Label>Restaurant ID{form.offerScope === 'MULTIPLE' ? 's (comma separated)' : ''}</Label>
                                        <Input value={form.offerResturantIds}
                                               onChange={(e) => setForm({...form, offerResturantIds: e.target.value})}
                                               placeholder="restaurantId1, restaurantId2"/>
                                    </div>
                                )}
                                <div>
                                    <Label>Discount Type</Label>
                                    <Select value={form.offerType}
                                            onValueChange={(v) => setForm({...form, offerType: v})}>
                                        <SelectTrigger><SelectValue/></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="PERCENTAGE">Percentage</SelectItem>
                                            <SelectItem value="FLAT">Flat amount</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Discount Value</Label>
                                    <Input type="number" min="0" value={form.offerValue}
                                           onChange={(e) => setForm({...form, offerValue: e.target.value})}/>
                                </div>
                                <div>
                                    <Label>Min Order (₹)</Label>
                                    <Input type="number" min="0" value={form.offerMinOrder}
                                           onChange={(e) => setForm({...form, offerMinOrder: e.target.value})}/>
                                </div>
                                <div>
                                    <Label>Max Discount (₹, for %)</Label>
                                    <Input type="number" min="0" value={form.offerMaxDiscount}
                                           onChange={(e) => setForm({...form, offerMaxDiscount: e.target.value})}/>
                                </div>
                                <div className="col-span-2">
                                    <Label>Offer Title</Label>
                                    <Input value={form.offerTitle}
                                           onChange={(e) => setForm({...form, offerTitle: e.target.value})}
                                           placeholder={`${form.brandName || form.fullName || 'Affiliate'} offer`}/>
                                </div>
                            </div>
                        )}

                        <Button type="submit" className="w-full" data-testid="affiliate-submit-button">
                            {editId ? 'Update Affiliate' : 'Create Affiliate'}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AffiliatesTab;
